import {
  METRIC_REGISTRY,
  formatMetricDelta,
  formatMetricValue,
  metricDefinition,
} from "./metricRegistry";

const RATE_METRICS = new Set([
  "assessment_completion_rate",
  "intervention_completion_rate",
  "follow_up_completion_rate",
  "positive_response_rate",
]);

const PRIMARY_DIMENSIONS = Object.freeze({
  assessment_completion_rate: ["cohort", "abandonmentSection", "program", "device"],
  intervention_completion_rate: ["intervention", "cohort", "program", "baselinePattern"],
  follow_up_completion_rate: ["cohort", "intervention", "program", "baselinePattern"],
  positive_response_rate: ["interventionPattern", "program", "intervention", "baselinePattern", "cohort"],
  average_response_change: ["program", "intervention", "baselinePattern", "cohort"],
});

const DIMENSION_LABELS = Object.freeze({
  cohort: "Cohort",
  program: "Program",
  abandonmentSection: "Abandonment section",
  device: "Device",
  intervention: "Intervention",
  baselinePattern: "Baseline pattern",
  interventionPattern: "Intervention and baseline pattern",
});

const finite = (value) => (Number.isFinite(value) ? value : null);

const dimensionValue = (record, dimension) => {
  if (dimension === "abandonmentSection") {
    return record.assessment?.completed
      ? "Completed assessment"
      : record.assessment?.abandonmentSection || "Unknown section";
  }
  if (dimension === "intervention") return record.intervention?.name || "No intervention";
  if (dimension === "interventionPattern") {
    return `${record.intervention?.name || "No intervention"} · ${record.baselinePattern || "Unknown pattern"}`;
  }
  return record[dimension] || "Unknown";
};

const metricParts = (records, key) => {
  if (key === "assessment_started_count") {
    return { numerator: records.filter((record) => record.assessment?.startedAt).length, denominator: 1 };
  }
  if (key === "assessment_completion_rate") {
    const eligible = records.filter((record) => record.assessment?.startedAt);
    return {
      numerator: eligible.filter((record) => record.assessment?.completed).length,
      denominator: eligible.length,
    };
  }
  if (key === "intervention_completion_rate") {
    const eligible = records.filter((record) => record.intervention?.assigned);
    return {
      numerator: eligible.filter((record) => record.intervention?.completed).length,
      denominator: eligible.length,
    };
  }
  if (key === "follow_up_completion_rate") {
    const eligible = records.filter((record) => record.followUp?.requested);
    return {
      numerator: eligible.filter((record) => record.followUp?.completed).length,
      denominator: eligible.length,
    };
  }
  if (key === "positive_response_rate") {
    const eligible = records.filter((record) => record.followUp?.completed);
    return {
      numerator: eligible.filter((record) => record.response?.positive).length,
      denominator: eligible.length,
    };
  }
  if (key === "average_response_change") {
    const changes = records
      .map((record) => record.response?.change)
      .filter((value) => Number.isFinite(value));
    return {
      numerator: changes.reduce((sum, value) => sum + value, 0),
      denominator: changes.length,
    };
  }
  return { numerator: 0, denominator: 0 };
};

const metricValueFromParts = (key, parts) => {
  if (key === "assessment_started_count") return parts.numerator;
  if (!parts.denominator) return null;
  return parts.numerator / parts.denominator;
};

export const calculateMetric = (records, key) => {
  const definition = metricDefinition(key);
  const parts = metricParts(records, key);
  const value = metricValueFromParts(key, parts);
  return {
    ...definition,
    value: finite(value),
    numerator: parts.numerator,
    denominator: key === "assessment_started_count" ? parts.numerator : parts.denominator,
    sampleSize: key === "assessment_started_count" ? parts.numerator : parts.denominator,
    sampleStatus:
      parts.denominator >= (definition?.minimumSample || 1) ? "sufficient" : "limited",
  };
};

export const calculateMetricSet = (records, period) => {
  const selected = records.filter((record) => record.period === period);
  return Object.fromEntries(
    METRIC_REGISTRY.map((definition) => [definition.key, calculateMetric(selected, definition.key)]),
  );
};

const groupByDimension = (records, dimension) => {
  const grouped = new Map();
  for (const record of records) {
    const value = dimensionValue(record, dimension);
    if (!grouped.has(value)) grouped.set(value, []);
    grouped.get(value).push(record);
  }
  return grouped;
};

const decomposeDimension = ({ records, metricKey, dimension, direction }) => {
  const comparison = groupByDimension(
    records.filter((record) => record.period === "comparison"),
    dimension,
  );
  const current = groupByDimension(
    records.filter((record) => record.period === "current"),
    dimension,
  );
  const labels = new Set([...comparison.keys(), ...current.keys()]);
  const segments = [];

  for (const label of labels) {
    const previousRecords = comparison.get(label) || [];
    const currentRecords = current.get(label) || [];
    const previous = calculateMetric(previousRecords, metricKey);
    const now = calculateMetric(currentRecords, metricKey);
    if (!Number.isFinite(previous.value) || !Number.isFinite(now.value) || !now.sampleSize) continue;
    const signedDifference = now.value - previous.value;
    const directionalDifference = direction < 0 ? -signedDifference : signedDifference;
    const impact = Math.max(0, directionalDifference * now.sampleSize);
    segments.push({
      dimension,
      dimensionLabel: DIMENSION_LABELS[dimension] || dimension,
      label,
      currentValue: now.value,
      comparisonValue: previous.value,
      change: signedDifference,
      sampleSize: now.sampleSize,
      comparisonSampleSize: previous.sampleSize,
      impact,
    });
  }

  const totalImpact = segments.reduce((sum, segment) => sum + segment.impact, 0);
  return segments
    .map((segment) => ({
      ...segment,
      contributionShare: totalImpact > 0 ? segment.impact / totalImpact : 0,
    }))
    .filter((segment) => segment.contributionShare > 0)
    .sort((a, b) => b.contributionShare - a.contributionShare);
};

const decomposeAbandonmentSections = (records) => {
  const comparisonFailures = records.filter(
    (record) => record.period === "comparison" && !record.assessment?.completed,
  );
  const currentFailures = records.filter(
    (record) => record.period === "current" && !record.assessment?.completed,
  );
  if (!comparisonFailures.length || !currentFailures.length) return [];
  const previous = groupByDimension(comparisonFailures, "abandonmentSection");
  const current = groupByDimension(currentFailures, "abandonmentSection");
  const labels = new Set([...previous.keys(), ...current.keys()]);
  const segments = [];
  for (const label of labels) {
    const previousCount = previous.get(label)?.length || 0;
    const currentCount = current.get(label)?.length || 0;
    const previousShare = previousCount / comparisonFailures.length;
    const currentShare = currentCount / currentFailures.length;
    const expectedCurrentCount = previousShare * currentFailures.length;
    segments.push({
      dimension: "abandonmentSection",
      dimensionLabel: DIMENSION_LABELS.abandonmentSection,
      label,
      currentValue: currentShare,
      comparisonValue: previousShare,
      change: currentShare - previousShare,
      sampleSize: currentFailures.length,
      comparisonSampleSize: comparisonFailures.length,
      impact: Math.max(0, currentCount - expectedCurrentCount),
    });
  }
  const totalImpact = segments.reduce((sum, segment) => sum + segment.impact, 0);
  return segments
    .map((segment) => ({
      ...segment,
      contributionShare: totalImpact > 0 ? segment.impact / totalImpact : 0,
    }))
    .filter((segment) => segment.contributionShare > 0)
    .sort((a, b) => b.contributionShare - a.contributionShare);
};

const confidenceFor = ({ sampleSize, magnitude }) => {
  if (sampleSize >= 100 && magnitude >= 0.08) return "high";
  if (sampleSize >= 40 && magnitude >= 0.05) return "moderate";
  return "limited";
};

const severityFor = ({ magnitude, sampleSize, threshold }) => {
  const ratio = threshold ? magnitude / threshold : 0;
  if (ratio >= 2 && sampleSize >= 80) return "high";
  if (ratio >= 1.2 && sampleSize >= 40) return "medium";
  return "watch";
};

const nextInvestigation = (metricKey) => {
  const messages = {
    assessment_completion_rate:
      "Review the highest-contributing cohort and the assessment section where incomplete records concentrate.",
    intervention_completion_rate:
      "Compare assignment-to-completion patterns by intervention and cohort before changing any intervention logic.",
    follow_up_completion_rate:
      "Inspect follow-up timing, cohort scheduling, and whether missed responses follow incomplete interventions.",
    positive_response_rate:
      "Compare the associated pattern by program, intervention, and baseline pattern; do not interpret the relationship as proof of causation.",
    average_response_change:
      "Inspect the response distribution and the segments contributing most to the period change.",
  };
  return messages[metricKey] || "Open the contribution tree and inspect the supporting aggregate records.";
};

const bestDecomposition = (records, metricKey, direction) => {
  const dimensions = PRIMARY_DIMENSIONS[metricKey] || ["program", "cohort"];
  const candidates = dimensions
    .map((dimension) => ({
      dimension,
      segments: decomposeDimension({ records, metricKey, dimension, direction }),
    }))
    .filter((candidate) => candidate.segments.length);
  candidates.sort((a, b) => {
    const aTop = a.segments[0]?.contributionShare || 0;
    const bTop = b.segments[0]?.contributionShare || 0;
    return bTop - aTop;
  });
  return { dimensions, primary: candidates[0] || { dimension: dimensions[0], segments: [] } };
};

export const buildRootCauseAnalysis = (records, insight) => {
  const metric = metricDefinition(insight.metricKey);
  const direction = insight.change < 0 ? -1 : 1;
  const { dimensions, primary } = bestDecomposition(records, insight.metricKey, direction);
  const secondaryDimension =
    insight.metricKey === "assessment_completion_rate" &&
    primary.dimension !== "abandonmentSection"
      ? "abandonmentSection"
      : dimensions.find((dimension) => dimension !== primary.dimension);
  const rootChildren = primary.segments.slice(0, 4).map((segment, index) => {
    const subsetLabels = new Set([segment.label]);
    const subset = records.filter(
      (record) => subsetLabels.has(dimensionValue(record, primary.dimension)),
    );
    const secondary = secondaryDimension
      ? (secondaryDimension === "abandonmentSection"
          ? decomposeAbandonmentSections(subset)
          : decomposeDimension({
              records: subset,
              metricKey: insight.metricKey,
              dimension: secondaryDimension,
              direction,
            })
        ).slice(0, 3)
      : [];
    return {
      id: `branch-${primary.dimension}-${index}`,
      ...segment,
      children: secondary.map((child, childIndex) => ({
        id: `branch-${primary.dimension}-${index}-${secondaryDimension}-${childIndex}`,
        ...child,
        children: [],
      })),
    };
  });

  const evidence = [];
  for (const branch of rootChildren) {
    evidence.push({
      id: `evidence-${evidence.length + 1}`,
      dimension: branch.dimensionLabel,
      segment: branch.label,
      currentValue: branch.currentValue,
      comparisonValue: branch.comparisonValue,
      sampleSize: branch.sampleSize,
      contributionShare: branch.contributionShare,
    });
    for (const child of branch.children) {
      evidence.push({
        id: `evidence-${evidence.length + 1}`,
        dimension: child.dimensionLabel,
        segment: child.label,
        currentValue: child.currentValue,
        comparisonValue: child.comparisonValue,
        sampleSize: child.sampleSize,
        contributionShare: child.contributionShare,
      });
    }
  }

  return {
    metric,
    root: {
      id: "root",
      label: insight.title,
      currentValue: insight.currentValue,
      comparisonValue: insight.comparisonValue,
      change: insight.change,
      sampleSize: insight.sampleSize,
      dimension: primary.dimension,
      dimensionLabel: DIMENSION_LABELS[primary.dimension] || primary.dimension,
      children: rootChildren,
    },
    evidence,
    analyticalBoundary:
      "The tree decomposes the observed change across synthetic segments. It identifies contribution and association, not a proven cause.",
  };
};

export const detectInsights = (records) => {
  const current = calculateMetricSet(records, "current");
  const comparison = calculateMetricSet(records, "comparison");
  const insights = [];

  for (const definition of METRIC_REGISTRY) {
    if (!definition.alertThreshold) continue;
    const now = current[definition.key];
    const before = comparison[definition.key];
    if (!Number.isFinite(now.value) || !Number.isFinite(before.value)) continue;
    const change = now.value - before.value;
    const magnitude = Math.abs(change);
    if (magnitude < definition.alertThreshold) continue;
    const sampleSize = Math.min(now.sampleSize, before.sampleSize);
    const direction = change < 0 ? "declined" : "increased";
    const confidence = confidenceFor({ sampleSize, magnitude });
    const severity = severityFor({
      magnitude,
      sampleSize,
      threshold: definition.alertThreshold,
    });
    const insight = {
      id: `insight-${definition.key}`,
      kind: "observed_change",
      metricKey: definition.key,
      metricName: definition.name,
      title: `${definition.name} ${direction}`,
      statement: `${definition.name} ${direction} from ${formatMetricValue(
        definition,
        before.value,
      )} to ${formatMetricValue(definition, now.value)} (${formatMetricDelta(
        definition,
        change,
      )}).`,
      currentValue: now.value,
      comparisonValue: before.value,
      change,
      percentChange: before.value ? change / Math.abs(before.value) : null,
      sampleSize,
      currentSampleSize: now.sampleSize,
      comparisonSampleSize: before.sampleSize,
      severity,
      confidence,
      affectedPeriod: "Current half versus comparison half",
      recommendedNext: nextInvestigation(definition.key),
      score: magnitude * Math.log10(Math.max(10, sampleSize)) * (severity === "high" ? 1.4 : 1),
    };
    insights.push(insight);
  }

  return insights.sort((a, b) => b.score - a.score);
};

export const buildDeterministicNarrative = (insight, rca) => {
  const top = rca?.root?.children?.[0];
  return {
    headline: insight.title,
    explanation: top
      ? `The observed change is concentrated in ${top.label} when the data is decomposed by ${
          top.dimensionLabel.toLowerCase()
        }. This segment should be investigated first, but the pattern does not establish a cause.`
      : "The calculated change passed the configured alert threshold, but no single synthetic segment dominates the available decomposition.",
    evidence: (rca?.evidence || []).slice(0, 3),
    nextInvestigation: insight.recommendedNext,
    limitation:
      "Synthetic demonstration data can validate the analytical workflow; it cannot establish real participant outcomes or intervention effectiveness.",
    source: "deterministic",
  };
};

export const buildNarrativePayload = (insight, rca) => ({
  insightId: insight.id,
  metric: insight.metricKey,
  currentValue: insight.currentValue,
  comparisonValue: insight.comparisonValue,
  absoluteChange: insight.change,
  sampleSize: insight.sampleSize,
  confidence: insight.confidence,
  analyticalBoundary: rca.analyticalBoundary,
  evidence: rca.evidence.slice(0, 8).map((item) => ({
    id: item.id,
    dimension: item.dimension,
    segment: item.segment,
    currentValue: item.currentValue,
    comparisonValue: item.comparisonValue,
    contributionShare: item.contributionShare,
    sampleSize: item.sampleSize,
  })),
});

export const analyzeSyntheticDataset = (dataset) => {
  const current = calculateMetricSet(dataset.records, "current");
  const comparison = calculateMetricSet(dataset.records, "comparison");
  const insights = detectInsights(dataset.records).map((insight) => ({
    ...insight,
    rca: buildRootCauseAnalysis(dataset.records, insight),
  }));
  return { current, comparison, insights };
};

export const isRateMetric = (key) => RATE_METRICS.has(key);
